import { Button, Card, Heading, IconButton, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import {
	type AssetTypeIdentifier,
	type IdentifierModel,
	OrganizationAccessLevel,
	type Stored,
} from "@overmap-ai/common"
import { selectAssetTypeById, selectAssetTypeIdentifiersOfAssetType } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { CreateIdentifierDialog } from "../../components/Dialogs/CreateIdentifierDialog"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput, useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId/identifiers")

export const AssetTypeSettingsIdentifiersView = memo(() => {
	const { assetTypeId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected assetType to exist, got ${assetType}.`)
	}

	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))

	const handleSubmit = useCallback(
		(name: IdentifierModel["name"]) => {
			void sdk.assetTypeIdentifiers.add({
				asset_type: assetType.uuid,
				name: name,
			})
		},
		[assetType.uuid],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Identifiers</Heading>
					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<CreateIdentifierDialog onSubmit={handleSubmit} size="sm">
							<Button variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Create
							</Button>
						</CreateIdentifierDialog>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<div role="list" className="flex flex-col gap-4">
					{assetTypeIdentifiers.length > 0 ? (
						assetTypeIdentifiers.map((assetTypeIdentifier) => {
							return (
								<AssetTypeIdentifierListItem
									assetTypeIdentifier={assetTypeIdentifier}
									key={assetTypeIdentifier.uuid}
								/>
							)
						})
					) : (
						<Text size="sm" weight="regular" accentColor="base">
							No identifiers.
						</Text>
					)}
				</div>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsIdentifiersView.displayName = "AssetTypeSettingsIdentifiersView"

interface AssetTypeIdentifierListItemProps {
	assetTypeIdentifier: Stored<AssetTypeIdentifier>
}

const AssetTypeIdentifierListItem = memo<AssetTypeIdentifierListItemProps>((props) => {
	const { assetTypeIdentifier } = props

	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const isAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)

	const handleAssetTypeIdentifierNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value

			void sdk.assetTypeIdentifiers.update({
				uuid: assetTypeIdentifier.uuid,
				name: value,
			})
		},
		[assetTypeIdentifier.uuid],
	)

	const assetTypeIdentifierNameInputProps = useBlurInput<HTMLInputElement>(
		assetTypeIdentifier.name,
		handleAssetTypeIdentifierNameInputBlur,
		true,
	)

	const handleDelete = useCallback(() => {
		openConfirmDeleteDialog({
			title: "Delete identifier",
			description: "The identifier will be deleted immediately. This action cannot be undone.",
			onAction: () => sdk.assetTypeIdentifiers.delete(assetTypeIdentifier.uuid),
		})
	}, [assetTypeIdentifier.uuid, openConfirmDeleteDialog])

	return (
		<Card className="flex items-center justify-between gap-2" role="listitem" size="sm">
			<Input.Root variant="outline">
				<Input.Slot>
					<LuIcon icon={modelIconMapping.identifier} />
				</Input.Slot>
				<Input.Field placeholder="Enter a name" disabled={!isAdmin} {...assetTypeIdentifierNameInputProps} />
			</Input.Root>

			<IconButton accentColor="base" size="sm" variant="ghost" disabled={!isAdmin} onClick={handleDelete}>
				<LuIcon icon={modelIconMapping.delete} />
			</IconButton>
		</Card>
	)
})
AssetTypeIdentifierListItem.displayName = "AssetTypeIdentifierListItem"
